import { PayloadAction, createSlice, current } from '@reduxjs/toolkit';

import { mapCatalogItemFromOrder } from 'entities/CatalogItem/lib/mapCatalogItemFromOrder';
import { fetchOrder } from 'entities/Order';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { find, has, remove, set, unset } from 'shared/lib/lodash/lodash';
import { revertCatalogStores } from 'shared/ui/store/resetStoresAction';

import {
    FETCH_ABORTED,
    GLOBAL_CATALOG_ITEMS_PAGE_SIZE,
    GLOBAL_CATALOG_MAX_PAGES,
} from '../consts/catalogItemsConsts';
import {
    buildGlobalCatalogQueryKey,
    createInitialGlobalCatalogSearchState,
    ensureGlobalCatalogSearchState,
    getGlobalCatalogTotalCount,
} from '../lib/globalCatalogSearch';
import { fetchCatalogItemById } from '../services/fetchCatalogItemById/fetchCatalogItemById';
import { fetchCatalogItems } from '../services/fetchCatalogItems/fetchCatalogItems';
import { fetchGlobalCatalogItems } from '../services/fetchGlobalCatalogItems/fetchGlobalCatalogItems';
import {
    CatalogItem,
    CatalogItemInOrder,
    CatalogItemSchema,
    GlobalCatalogItem,
    NewlyAddedItem,
} from '../types/CatalogItemSchema';

/** Aligns with `getItemUid` when uuid exists; falls back to catalog row id. */
const selectionKeyFromFrozen = (frozen: CatalogItem) =>
    frozen.uuid ?? frozen.id;

const initialState: CatalogItemSchema = {
    listIsLoading: false,
    globalCatalogSearch: createInitialGlobalCatalogSearchState(),
    showDetailedView: false,
    selectedItems: {},
    newAddedItems: {},
    proposalsLoading: false,
};

export const CatalogItemSlice = createSlice({
    name: 'CatalogItem',
    initialState,
    reducers: {
        toggleMultiselectItem: (state, action: PayloadAction<string>) => {
            const newData = { ...state.selectedItems };
            const id = action.payload;

            if (has(newData, id)) {
                unset(newData, id);
            } else {
                set(newData, id, { qty: 1 });
            }

            state.selectedItems = newData;
        },
        addMultiselectItem: (
            state,
            action: PayloadAction<{
                /** Key in `selectedItems` (checkbox `selectionKey`). */
                id: string;
                uid?: string;
                catalogItemId?: string;
                title?: string;
                propertyCategoryId?: string;
            }>,
        ) => {
            const newData = { ...state.selectedItems };
            const { id, uid, catalogItemId, title, propertyCategoryId } =
                action.payload;

            if (!has(newData, id)) {
                const entry: Record<string, unknown> = { qty: 1 };
                if (uid !== undefined) entry.uid = uid;
                if (catalogItemId !== undefined)
                    entry.catalogItemId = catalogItemId;
                if (title !== undefined) entry.title = title;
                if (propertyCategoryId !== undefined)
                    entry.propertyCategoryId = propertyCategoryId;
                set(newData, id, entry);
                state.selectedItems = newData;
            }
        },
        addNewItem: (state, action: PayloadAction<NewlyAddedItem>) => {
            const { itemId } = action.payload;
            state.newAddedItems[itemId] = action.payload;
        },
        removeNewItem: (state, action: PayloadAction<string>) => {
            if (has(state.newAddedItems, action.payload)) {
                delete state.newAddedItems[action.payload];
            }
        },
        clearNewItems: (state) => {
            state.newAddedItems = {};
        },
        removeMultiselectItem: (state, action: PayloadAction<string>) => {
            const newData = { ...state.selectedItems };
            const id = action.payload;

            if (has(newData, id)) {
                unset(newData, id);
                state.selectedItems = newData;
            }
        },
        removeItem: (state, action: PayloadAction<string>) => {
            const newSelectedItems = { ...state.selectedItems };
            const newData = [...(state?.dataList || [])] as CatalogItem[];
            const idOfDeleteItem = action.payload;

            if (has(newSelectedItems, idOfDeleteItem)) {
                unset(newSelectedItems, idOfDeleteItem);
                state.selectedItems = newSelectedItems;
            }
            remove(newData, (item) => {
                const row = item as CatalogItem | GlobalCatalogItem;
                if ('id' in row) {
                    return (
                        row.id === idOfDeleteItem || row.uuid === idOfDeleteItem
                    );
                }
                return row.uid === idOfDeleteItem;
            });

            state.dataList = newData;
        },
        clearAllItems: (state) => {
            state.dataList = [];
        },
        setShowDetailedView: (state, action: PayloadAction<boolean>) => {
            state.showDetailedView = action.payload;
        },
        setSelectedItem: (state, action: PayloadAction<CatalogItem>) => {
            state.selectedItem = action.payload;
        },
        setSelectedItemById: (state, action: PayloadAction<string>) => {
            const idToSelect = action.payload;
            state.selectedItem = find(
                state.dataList,
                (item: CatalogItem | GlobalCatalogItem) =>
                    'id' in item
                        ? item.id === idToSelect || item.uuid === idToSelect
                        : item.uid === idToSelect,
            );
        },
        closeDetailedItem: (state) => {
            state.selectedItem = null;
            if (state.showDetailedView) {
                state.showDetailedView = false;
            }
        },
        removeSingleItemByUidFromSelection: (
            state,
            action: PayloadAction<string>,
        ) => {
            const newData = { ...state.selectedItems };

            const idToRemove = Object.entries(newData).find(
                ([itemId, item]) => item.uid == action.payload,
            );

            if (idToRemove) {
                unset(newData, idToRemove[0]);
                state.selectedItems = newData;
            }
        },
        removeMultipleItemsByUid: (state, action: PayloadAction<string[]>) => {
            const newData = [...state.dataList] as CatalogItem[];
            const deletedItemUuids = action.payload;

            state.dataList = newData.filter((item) => {
                return !deletedItemUuids.includes(item.uuid);
            });
        },
        updateItems: (
            state,
            action: PayloadAction<Record<CatalogItem['id'], CatalogItem>>,
        ) => {
            const updatedData = (state.dataList as CatalogItem[]).map(
                (item) => {
                    if (item.id in action.payload) {
                        return action.payload[item.id];
                    }
                    return item;
                },
            );

            const updatedDataArray = Object.entries(action.payload);

            updatedDataArray.forEach(([id, item]) => {
                set(updatedData, id, item);
            });

            state.dataList = updatedData;
        },
        updateItem: (state, action: PayloadAction<CatalogItem>) => {
            const updatedList = [...state.dataList] as CatalogItem[];
            const updatedItem = action.payload;

            const foundIndex = updatedList.findIndex(
                (item) => item.id === updatedItem.id,
            );

            if (foundIndex || foundIndex === 0) {
                updatedList[foundIndex] = updatedItem;
            }

            state.dataList = updatedList;
        },
        setItemQuantity: (state, action) => {
            const newData = { ...state.selectedItems };
            const { id, value } = action.payload;

            if (has(newData, id)) {
                set(newData, `${id}.qty`, value);
            }

            state.selectedItems = newData;
        },
        setSelection: (
            state,
            action: PayloadAction<{ items: CatalogItemInOrder[] }>,
        ) => {
            const { items } = action.payload;
            const newSelectedItems = {};

            items.forEach((item) => {
                const catalogItemData = mapCatalogItemFromOrder(item);
                if (!catalogItemData) {
                    return;
                }
                set(
                    newSelectedItems,
                    selectionKeyFromFrozen(item.itemInfoFrozen),
                    catalogItemData.selection,
                );
            });

            state.selectedItems = newSelectedItems;
        },
        removeAllSelection: (state) => {
            state.selectedItems = {};
            state.newAddedItems = {};
        },
        setLoading: (state, action) => {
            state.listIsLoading = action.payload;
        },
        setProposalsLoading: (state, action: PayloadAction<boolean>) => {
            state.proposalsLoading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(revertCatalogStores, () => initialState)
            .addCase(fetchOrder.fulfilled, (state, action) => {
                const { data, isCatalog } = action.payload;
                state.listIsLoading = false;

                const currentlySelectedItem = current(state)
                    .selectedItem as CatalogItem;
                const newData: CatalogItem[] = [];
                const newSelectedItems = {};

                data.items.forEach((item) => {
                    const catalogItemData = mapCatalogItemFromOrder(item);
                    if (!catalogItemData) {
                        return;
                    }
                    newData.push(catalogItemData.info);

                    const selectionKey = isCatalog
                        ? selectionKeyFromFrozen(item.itemInfoFrozen)
                        : item.id;
                    set(
                        newSelectedItems,
                        selectionKey,
                        catalogItemData.selection,
                    );
                    if (
                        currentlySelectedItem != null &&
                        (selectionKey === currentlySelectedItem.uuid ||
                            selectionKey === currentlySelectedItem.id)
                    ) {
                        state.selectedItem = catalogItemData.info;
                    }
                });

                state.selectedItems = newSelectedItems;
                state.dataList = newData;
            })
            .addCase(fetchOrder.rejected, (state, action) => {
                state.listIsLoading = false;
                state.error = action.payload;
            })
            .addCase(editOrderItem.fulfilled, (state, action) => {
                const orderItem = action.payload;
                const catalogItemData = mapCatalogItemFromOrder(orderItem);
                if (!catalogItemData) {
                    state.listIsLoading = false;
                    return;
                }
                const selectionKey = selectionKeyFromFrozen(
                    orderItem.itemInfoFrozen,
                );

                const newSelectedItems = { ...state.selectedItems };
                set(newSelectedItems, selectionKey, catalogItemData.selection);

                const findIndex = state.dataList.findIndex((item) => {
                    if ('uuid' in item) {
                        return (
                            item.uuid === selectionKey ||
                            item.id === selectionKey
                        );
                    }
                    return 'uid' in item && item.uid === selectionKey;
                });

                if (findIndex || findIndex === 0) {
                    state.dataList[findIndex] = catalogItemData.info;
                    state.selectedItem = catalogItemData.info;
                }

                state.selectedItems = newSelectedItems;
                state.listIsLoading = false;
            })
            .addCase(fetchGlobalCatalogItems.pending, (state, action) => {
                const {
                    searchString,
                    categoryId,
                    propertyId,
                    page = 1,
                    pageSize = GLOBAL_CATALOG_ITEMS_PAGE_SIZE,
                } = action.meta.arg;

                const globalCatalogSearch =
                    ensureGlobalCatalogSearchState(state);

                state.error = null;

                globalCatalogSearch.pageSize = pageSize;
                if (page > 1) {
                    globalCatalogSearch.isLoadingMore = true;
                    return;
                }

                state.listIsLoading = true;
                globalCatalogSearch.page = 1;
                globalCatalogSearch.hasNextPage = false;
                globalCatalogSearch.totalCount = 0;
                globalCatalogSearch.endCursor = '';
                globalCatalogSearch.isLoadingMore = false;
                globalCatalogSearch.queryKey = buildGlobalCatalogQueryKey({
                    searchString,
                    categoryId,
                    propertyId,
                });
            })
            .addCase(fetchGlobalCatalogItems.fulfilled, (state, action) => {
                const {
                    searchString,
                    categoryId,
                    propertyId,
                    page = 1,
                    pageSize = GLOBAL_CATALOG_ITEMS_PAGE_SIZE,
                } = action.meta.arg;

                const globalCatalogSearch =
                    ensureGlobalCatalogSearchState(state);
                const isLoadMoreRequest = page > 1;
                const requestQueryKey = buildGlobalCatalogQueryKey({
                    searchString,
                    categoryId,
                    propertyId,
                });

                if (
                    isLoadMoreRequest &&
                    requestQueryKey !== globalCatalogSearch.queryKey
                ) {
                    globalCatalogSearch.isLoadingMore = false;
                    return;
                }

                const totalCount = getGlobalCatalogTotalCount(action.payload);
                const hasNextPage = action.payload.hasNextPage;
                const hasReachedMaxPage = page >= GLOBAL_CATALOG_MAX_PAGES;
                const newItems = action.payload.items;

                state.listIsLoading = false;

                globalCatalogSearch.isLoadingMore = false;
                globalCatalogSearch.page = page;
                globalCatalogSearch.pageSize = pageSize;
                globalCatalogSearch.hasNextPage =
                    hasNextPage && !hasReachedMaxPage;
                globalCatalogSearch.totalCount = totalCount;
                globalCatalogSearch.endCursor = action.payload.endCursor ?? '';
                globalCatalogSearch.queryKey = requestQueryKey;

                if (isLoadMoreRequest && Array.isArray(state.dataList)) {
                    const existingItems = state.dataList as GlobalCatalogItem[];
                    const existingUidSet = new Set(
                        existingItems
                            .filter((item) => 'uid' in item)
                            .map((item) => item.uid),
                    );

                    const itemsToAppend = newItems.filter(
                        (item) => !existingUidSet.has(item.uid),
                    );

                    state.dataList = [...existingItems, ...itemsToAppend];
                    return;
                }

                state.dataList = newItems;
            })
            .addCase(fetchGlobalCatalogItems.rejected, (state, action) => {
                const globalCatalogSearch =
                    ensureGlobalCatalogSearchState(state);

                const page = action.meta.arg?.page ?? 1;
                const isLoadMoreRequest = page > 1;

                if (action.payload === FETCH_ABORTED) {
                    state.listIsLoading = false;
                    if (!isLoadMoreRequest) {
                        globalCatalogSearch.page = 1;
                        globalCatalogSearch.hasNextPage = false;
                        globalCatalogSearch.totalCount = 0;
                        globalCatalogSearch.endCursor = '';
                    }
                    globalCatalogSearch.isLoadingMore = false;
                    return;
                }

                if (isLoadMoreRequest) {
                    globalCatalogSearch.isLoadingMore = false;
                    state.error = action.payload;
                    return;
                }

                state.listIsLoading = false;
                globalCatalogSearch.isLoadingMore = false;
                globalCatalogSearch.hasNextPage = false;
                globalCatalogSearch.totalCount = 0;
                globalCatalogSearch.endCursor = '';
                state.dataList = null;
                state.error = action.payload;
            })
            .addCase(fetchCatalogItems.pending, (state) => {
                state.error = null;
                state.listIsLoading = true;
            })
            .addCase(fetchCatalogItems.fulfilled, (state, action) => {
                state.listIsLoading = false;
                state.dataList = action.payload;
            })
            .addCase(fetchCatalogItems.rejected, (state, action) => {
                if (action.payload === FETCH_ABORTED) {
                    return;
                }
                state.listIsLoading = false;
                state.dataList = null;
                state.error = action.payload;
            })
            .addCase(fetchCatalogItemById.pending, (state) => {
                state.proposalsLoading = true;
                state.error = null;
            })
            .addCase(fetchCatalogItemById.fulfilled, (state, action) => {
                state.proposalsLoading = false;

                if (
                    state.selectedItem &&
                    'id' in state.selectedItem &&
                    state.selectedItem.id !== action.payload.id
                ) {
                    return;
                }

                state.selectedItem = action.payload;
                if (state.dataList && Array.isArray(state.dataList)) {
                    const index = state.dataList.findIndex(
                        (item) => 'id' in item && item.id === action.payload.id,
                    );
                    if (index !== -1) {
                        const updatedList = [
                            ...state.dataList,
                        ] as CatalogItem[];
                        updatedList[index] = action.payload;
                        state.dataList = updatedList;
                    }
                }
            })
            .addCase(fetchCatalogItemById.rejected, (state, action) => {
                state.proposalsLoading = false;
                state.error = action.payload;
            });
    },
});

export const { actions: catalogItemActions } = CatalogItemSlice;
export const { reducer: catalogItemReducer } = CatalogItemSlice;
